/**
 * "Where is this phone right now?" for placing a static camera: the location-sharing fix when it is
 * fresh and good, otherwise one high-accuracy reading.
 */
import type { LatLon } from '@apos/shared';
import { serverNow } from '../../lib/clock';
import { locationSharing } from '../../lib/geolocation';

export interface PhoneFix extends LatLon {
  accuracyM: number | null;
}

/** A shared fix newer than this, and at least this good, is used as-is. */
const SHARED_MAX_AGE_MS = 15_000;
const SHARED_MAX_ACCURACY_M = 25;

export function getPhoneFix(geo: Geolocation | null = typeof navigator !== 'undefined' ? (navigator.geolocation ?? null) : null): Promise<PhoneFix> {
  const shared = locationSharing.getSnapshot().lastFix;
  if (shared && serverNow() - shared.t < SHARED_MAX_AGE_MS && shared.accuracyM != null && shared.accuracyM <= SHARED_MAX_ACCURACY_M) {
    return Promise.resolve({ lat: shared.lat, lon: shared.lon, accuracyM: shared.accuracyM });
  }
  if (!geo) return Promise.reject(new Error('This browser cannot read the phone’s location. Place the camera on the map instead.'));
  return new Promise((resolve, reject) => {
    geo.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? 'Location is off for this app. Turn it on in Settings, or place the camera on the map.'
              : err.code === err.TIMEOUT
                ? 'No GPS fix yet — step into the open and try again.'
                : 'Position unavailable — try again, or place the camera on the map.',
          ),
        ),
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
    );
  });
}
